<template>
  <aside class="order-list">
    <div class="list-header">
      <div class="search-box">
        <span class="fas fa-magnifying-glass"></span>
        <input v-model="selfSearch" type="search" placeholder="N°, titre, client, devis..." />
      </div>
      <div class="stages">
        <button v-for="stage in stageButtons" :key="stage.value" type="button" class="stage-chip" :class="{active: selfStage === stage.value}" @click="selfStage = stage.value">
          <span v-if="stage.value" class="dot" :style="{backgroundColor: color(stage.value)}"></span>{{ stage.label }} <span class="count">{{ stage.value ? countOf(stage.value) : orders.length }}</span>
        </button>
      </div>
    </div>

    <ul class="orders" v-if="visibleOrders.length > 0">
      <li v-for="order in visibleOrders" :key="order.id" class="order" :class="{selected: order.id === selectedId}" @click="selectCallback(order.id)">
        <span class="stage-bar" :style="{backgroundColor: color(order.stage)}"></span>
        <div class="order-main">
          <div class="order-top">
            <span class="order-number">N°{{ order.number }}</span>
            <span class="order-title" :title="order.title">{{ order.title || 'Sans titre' }}</span>
          </div>
          <p class="order-clients" v-if="order.clientNames && order.clientNames.length > 0">{{ order.clientNames.join(' · ') }}</p>
          <div class="order-figures">
            <StatusBadge :status="order.stage" :label="stageLabel(order.stage)" />
            <span v-if="order.finance.sold" class="figure" title="Vendu HT">{{ formatAmount(order.finance.sold) }}</span>
            <span v-if="order.finance.hasPurchase && order.finance.count > 0" class="figure margin" :class="{negative: order.finance.margin < 0}" title="Marge HT">
              Marge {{ order.finance.marginRate !== undefined ? formatRate(order.finance.marginRate) : formatAmount(order.finance.margin) }}
            </span>
          </div>
        </div>
      </li>
    </ul>
    <p v-else class="empty">Aucun dossier ne correspond à la recherche</p>
  </aside>
</template>

<script>
import {library} from '@fortawesome/fontawesome-svg-core';
import {faMagnifyingGlass} from "@fortawesome/free-solid-svg-icons";
import StatusBadge from "@/components/tables/commons/StatusBadge.vue";
import {getStatusBackgroundColor} from "@/components/tables/commons/statusColors";
import {formatAmount, formatRate} from "@/purchase/utils";
import {ORDER_STAGE, ORDER_STAGES, stageLabel} from "@/components/production/productionUtils";
library.add(faMagnifyingGlass);

const normalize = text => String(text ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

// Liste des dossiers : recherche libre et filtre par étape. Un clic sélectionne le dossier (selectCallback).
export default {
  name: 'OrderList',
  components: {StatusBadge},
  props: ['orders', 'selectedId', 'selectCallback'],
  data() {
    return {
      ORDER_STAGES,
      selfSearch: '',
      // Par défaut, seuls les dossiers en cours sont listés
      selfStage: ORDER_STAGE.IN_PROGRESS.value,
    };
  },
  computed: {
    // Création, Acceptée, En cours, Terminée, puis « Tous » et « Abandonné » en dernier
    stageButtons() {
      const aborted = ORDER_STAGE.ABORTED.value;
      return [
        ...ORDER_STAGES.filter(stage => stage.value !== aborted),
        {value: '', label: 'Tous'},
        ORDER_STAGE.ABORTED,
      ];
    },
    visibleOrders() {
      const search = normalize(this.selfSearch.trim());
      return this.orders.filter(order => (!this.selfStage || order.stage === this.selfStage) && (!search || order.searchText.includes(search)));
    },
  },
  watch: {
    // Un dossier ouvert depuis ailleurs (lien, URL) doit rester visible : le filtre bascule sur son étape
    selectedId(id) {
      const order = this.orders.find(candidate => candidate.id === id);
      if (order && this.selfStage && order.stage !== this.selfStage) this.selfStage = order.stage;
    },
  },
  methods: {
    formatAmount,
    formatRate,
    stageLabel,
    countOf(stage) {
      return this.orders.filter(order => order.stage === stage).length;
    },
    // Couleur de l'étape, la même que celle des pastilles de statut
    color(stage) {
      return getStatusBackgroundColor(stage);
    },
  },
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

.order-list
  display flex
  flex-direction column
  min-height 0
  background white
  border 1px solid #e3e7ef
  border-radius 14px
  overflow hidden

.list-header
  display flex
  flex-direction column
  gap 10px
  padding 12px
  border-bottom 1px solid #eef0f5
  background #fafbfd

.search-box
  display flex
  align-items center
  gap 8px
  padding 0 12px
  height CTRL_HEIGHT_SM + 8px
  background CTRL_BG
  border 1px solid CTRL_BORDER
  border-radius CTRL_RADIUS
  transition border-color CTRL_EASE, background CTRL_EASE

  &:focus-within
    background CTRL_BG_FOCUS
    border-color CTRL_FOCUS
    box-shadow 0 0 0 3px CTRL_FOCUS_RING

  span
    color CTRL_PLACEHOLDER
    font-size 13px

  input
    flex 1
    min-width 0
    border none
    outline none
    background transparent
    font-size 14px
    color CTRL_TEXT

.stages
  display flex
  flex-wrap wrap
  gap 6px

.stage-chip
  display flex
  align-items center
  gap 6px
  padding 4px 10px
  border 1px solid #dce2ee
  border-radius 20px
  background white
  color #4a5670
  font-size 12px
  font-weight 600
  cursor pointer
  transition all .15s ease

  &:hover
    background #f1f4fa

  &.active
    background THEME_COLOR_PRIMARY
    border-color THEME_COLOR_PRIMARY
    color THEME_COLOR_PRIMARY_OPPOSITE

    .count
      background rgba(255, 255, 255, 0.22)
      color THEME_COLOR_PRIMARY_OPPOSITE

  .dot
    width 8px
    height 8px
    border-radius 50%
    box-shadow inset 0 0 0 1px rgba(0, 0, 0, 0.12)

  .count
    padding 0 6px
    border-radius 10px
    background #eef1f7
    font-size 11px
    color #6b7590

.orders
  flex 1
  margin 0
  padding 6px
  list-style none
  overflow-y auto

.order
  position relative
  display flex
  gap 10px
  margin-bottom 4px
  padding 10px 12px 10px 8px
  border-radius 10px
  cursor pointer
  transition background .12s ease

  &:hover
    background #f3f5fa

  &.selected
    background #e8edf8
    box-shadow inset 0 0 0 1px #c5d0ea

.stage-bar
  flex 0 0 4px
  border-radius 4px
  box-shadow inset 0 0 0 1px rgba(0, 0, 0, 0.08)

.order-main
  flex 1
  min-width 0

.order-top
  display flex
  align-items baseline
  gap 8px

.order-number
  flex 0 0 auto
  font-weight 700
  color THEME_COLOR_PRIMARY

.order-title
  min-width 0
  overflow hidden
  white-space nowrap
  text-overflow ellipsis
  font-weight 600
  color FONT_COLOR

.order-clients
  margin 2px 0 0
  overflow hidden
  white-space nowrap
  text-overflow ellipsis
  font-size 12.5px
  color #7a849b

.order-figures
  display flex
  align-items center
  flex-wrap wrap
  gap 4px 10px
  margin-top 6px
  font-size 12.5px

.figure
  color #4a5670
  font-weight 600

  &.margin
    color #1f7a45

    &.negative
      color #c0392b

.empty
  margin 0
  padding 24px 16px
  text-align center
  color #8a93a8
  font-size 14px
</style>
